import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import CloseIcon from "@assets/icons/cross.svg";
import mediaQuery from "@utils/mediaQuery";

const DISMISS_KEY = "pwa-install-dismissed";
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (Date.now() - dismissedAt < DISMISS_DURATION_MS) return true;
    localStorage.removeItem(DISMISS_KEY);
    return false;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--bg-card);
  border-top: 1px solid var(--border-default, #e2e8f0);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  ${mediaQuery("tablet")`
    padding: 16px 40px;
  `}
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.p`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 2px;
  line-height: var(--line-height-lg);
`;

const Description = styled.p`
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  margin: 0;
  line-height: var(--line-height-md);
`;

const InstallButton = styled.button`
  background: var(--bg-header);
  color: var(--text-inverse);
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const StyledCloseIcon = styled(CloseIcon)`
  width: 20px;
  height: 20px;
`;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // localStorage unavailable
    }
  }, []);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari: show guidance
    if (isIOSSafari()) {
      setShowIOSPrompt(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    dismiss();
  };

  if (!visible) return null;

  return (
    <Banner>
      <Content>
        <Title>Add Te Whata Kura to your home screen</Title>
        <Description>
          {showIOSPrompt
            ? "Tap the Share button, then \"Add to Home Screen\" for quick access."
            : "Install this app for quick access to antibiotic guidelines."}
        </Description>
      </Content>
      {deferredPrompt && <InstallButton onClick={handleInstall}>Install</InstallButton>}
      <CloseButton onClick={dismiss} aria-label="Dismiss install prompt">
        <StyledCloseIcon />
      </CloseButton>
    </Banner>
  );
};

export default InstallPrompt;